'use client';

import LocaleSwitcher from '@/components/LocaleSwitcher';
import { HeaderAuthSection } from '@/components/auth';
import { HamburgerMenu } from '@/components/layout/HamburgerMenu';
import { HAMBURGER_MENU_PAGES } from '@/components/layout/footerNavLinks';
import { ThemeSwitcher } from '@/components/theme/ThemeSwitcher';
import { Link, usePathname } from '@/i18n/routing';
import Logo from '@/lib/logo/Logo';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';

// Create a reusable component for navigation links
function NavigationLinks({ onItemClick }: { onItemClick?: () => void }) {
  return <HeaderAuthSection onItemClick={onItemClick} />;
}

// Pages where locale and theme switchers should be hidden
const HIDE_SWITCHERS_PAGES = ['/document', '/dokument'];

interface SiteHeaderProps {
  /**
   * Include the advocate landing page (#7230) link in the hamburger menu —
   * launch checklist item 3 (#7951). Resolved from the build-time
   * `ADVOCATE_LANDING_ENABLED` env in the server `Header` wrapper, because the
   * value has no `NEXT_PUBLIC_` prefix and is not readable from client code.
   */
  includeAdvocateLanding?: boolean;
}

export function SiteHeader({
  includeAdvocateLanding = false,
}: SiteHeaderProps) {
  const pathname = usePathname();
  const [isScrolled, setIsScrolled] = useState(false);

  // Marketing pages show the hamburger at every width — it carries the
  // footer/legal nav those pages want reachable from the top bar.
  const isMarketingPage = HAMBURGER_MENU_PAGES.some((page) =>
    pathname.startsWith(page)
  );

  // Hide locale and theme switchers on document pages
  const showSwitchers = !HIDE_SWITCHERS_PAGES.some((page) =>
    pathname.startsWith(page)
  );

  // The advocate-directory LISTING renders a full-viewport (h-screen)
  // app-shell layout with its own top bar, which conflicts with this sticky
  // 10vh marketing header stacked above it on desktop. Hide the marketing
  // header at ≥mobile-view on that exact route so the listing owns the full
  // viewport — matching the real app shell, which has no marketing top bar.
  // (This also pre-empts the duplicate Lexomat logo once the directory's app
  // nav rail #5743 lands: the rail carries its own logo, so the marketing one
  // here would otherwise show twice on desktop.) Below mobile-view the listing
  // collapses to a single column and relies on the marketing header for
  // navigation, so it must stay. Exact match only: the detail / dashboard /
  // claim sub-routes use the normal page layout and must keep the header at
  // every width.
  //
  // Canonical listing is `/advokati` (sk `/advokati`) since #6323 — the old
  // all-professions `/professional-directory` listing now 301-redirects here,
  // so only `/advokati` ever reaches this component. `usePathname` is the
  // locale-stripped next-intl router, so `/sk/advokati` arrives as `/advokati`.
  const hideHeaderForDirectory = pathname === '/advokati';

  useEffect(() => {
    const mainContent = document.getElementById('main-content');
    // Also check for grid layout main content in custom header design
    const gridMainContent = document.querySelector('.mainContent');

    const handleScroll = () => {
      if (mainContent && mainContent.scrollTop > 0) {
        setIsScrolled(true);
      } else if (
        gridMainContent &&
        (gridMainContent as HTMLElement).scrollTop > 0
      ) {
        setIsScrolled(true);
      } else if (window.scrollY > 0) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    // Listen to all possible scroll targets
    const scrollTargets: (HTMLElement | Window)[] = [window];
    if (mainContent) scrollTargets.push(mainContent);
    if (gridMainContent) scrollTargets.push(gridMainContent as HTMLElement);

    scrollTargets.forEach((target) => {
      target.addEventListener('scroll', handleScroll);
    });

    // Initial check in case the page loads scrolled
    handleScroll();

    return () => {
      scrollTargets.forEach((target) => {
        target.removeEventListener('scroll', handleScroll);
      });
    };
  }, []);

  return (
    <>
      <header
        id='app-header'
        className={cn(
          'sticky top-0 w-full h-[10vh] bg-background/98 backdrop-blur-md transition-all duration-300 z-50 border-b border-transparent',
          isScrolled && 'shadow-lg border-border/50 bg-background',
          hideHeaderForDirectory && 'mobile-view:hidden'
        )}
      >
        <div className='mx-auto h-full flex items-center justify-between px-4 sm:px-6 py-4'>
          {/* Left Section - Logo with consistent padding */}
          <div className='flex items-center min-w-0 flex-shrink-0'>
            {/* #6057: brand logo → "/" (canonical home; "/" 308-redirects to
                /chat). Matches the in-app rail/SidebarBrand/New-chat which #6056
                standardized on "/". */}
            <Link href='/' className='flex items-center space-x-2'>
              <Logo />
            </Link>
          </div>

          {/* Right Section - Theme + Language Switcher + Auth with symmetric spacing */}
          <div className='flex items-center gap-4'>
            {showSwitchers && (
              <>
                {/* Theme Switcher */}
                <ThemeSwitcher />
                {/* Language Switcher with consistent spacing */}
                <LocaleSwitcher />
              </>
            )}

            {/* Auth row. Hidden below `sm`: at phone width it would collide
                with the logo and the theme/locale switchers. The hamburger
                below carries it instead — the two are complements, and
                changing one without the other is what produced #11039. */}
            <nav className='hidden sm:flex items-center'>
              <NavigationLinks />
            </nav>

            {/* Hamburger. ALWAYS rendered, because it is the only way to
                reach BOTH auth actions below `md`. On marketing pages it also
                shows at ≥md for the footer/legal nav; everywhere else it is
                `md:hidden`, so desktop is unchanged.

                `md`, not `sm`, is deliberate and was measured. The nav above
                un-hides at `sm` (640px) — but it un-hides SIGN-IN only,
                because DirectAuthButtons keeps sign-up behind `hidden md:flex`
                for the inline row. Scoping the hamburger to `sm:hidden` left
                640–767px with a sign-in button, no sign-up and no menu, i.e.
                registration unreachable in that band. The rule this encodes:
                the hamburger is present wherever EITHER auth action is hidden,
                not merely where both are.

                Rendering this unconditionally is deliberate. It used to be
                gated on HAMBURGER_MENU_PAGES alone, so every route missing
                from that allowlist — the whole advocate directory, which
                carries ~all of our organic traffic — had no menu AND no auth
                at phone width. An allowlist cannot express "the header must
                always offer a way in"; this can.

                Switchers stay off: this header renders its own beside the
                trigger. */}
            <div className={cn(!isMarketingPage && 'md:hidden')}>
              <HamburgerMenu
                showSwitchers={false}
                includeAdvocateLanding={includeAdvocateLanding}
                includeAuth
              />
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
